import Blockquote from '@tiptap/extension-blockquote';
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import Code from '@tiptap/extension-code';
import { Color } from '@tiptap/extension-color';
import Document from '@tiptap/extension-document';
import Dropcursor from '@tiptap/extension-dropcursor';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import Highlight from '@tiptap/extension-highlight';
import History from '@tiptap/extension-history';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Italic from '@tiptap/extension-italic';
import TiptapLink from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Strike from '@tiptap/extension-strike';
import { TaskItem } from '@tiptap/extension-task-item';
import { TaskList } from '@tiptap/extension-task-list';
import Text from '@tiptap/extension-text';
import { TextStyle } from '@tiptap/extension-text-style';
import TiptapUnderline from '@tiptap/extension-underline';
import { TrailingNode } from '@tiptap/extensions';
import AutoJoiner from 'tiptap-extension-auto-joiner';
import GlobalDragHandle from 'tiptap-extension-global-drag-handle';
import { Markdown } from 'tiptap-markdown';

import { classList } from '@/utils/cn';

import { AnimateInsertions } from './animate-insertions';
import { CodeBlockShiki } from './codeblock';
import { CursorRestore } from './cursor-restore';
import CustomKeymap from './custom-keymap';
import { DragSelectionHide } from './drag-selection-hide';
import {
  ImageUploadExtension,
  type ImageUploadExtensionOptions,
} from './image-upload';
import { LinkHoverExtension } from './link-hover';
import { Mathematics } from './mathematics';
import { Placeholder } from './placeholder';
import { SearchAndReplace } from './search-and-replace';
import { defaultSlashCommand } from './slash-command';
import { tableExtensions } from './table';
import UpdatedImage from './updated-image';

import type { Node } from '@tiptap/pm/model';
import type { MarkdownSerializerState } from 'prosemirror-markdown';

export * from './animate-insertions';
export * from './cursor-restore';
export * from './slash-command';
export * from './link-hover';
export * from './placeholder';

export {
  Bold,
  Italic,
  Strike,
  Code,
  Heading,
  Blockquote,
  BulletList,
  OrderedList,
  UpdatedImage,
};

const HighlightExtension = Highlight.configure({
  multicolor: true,
});

const MarkdownExtension = Markdown.configure({
  html: false,
  transformCopiedText: true,
  transformPastedText: true,
  // Keep single newlines as line breaks instead of collapsing them.
  breaks: true,
});

const codeblockShiki = CodeBlockShiki.configure({
  defaultTheme: 'github-dark',
  themes: {
    light: 'github-light',
    dark: 'github-dark',
  },
});

const document = Document;
const text = Text;
const paragraph = Paragraph;

const bold = Bold.configure({
  HTMLAttributes: {
    class: 'text-inherit',
  },
});

const italic = Italic;
const strike = Strike;

const code = Code.configure({
  HTMLAttributes: {
    spellcheck: 'false',
    class: classList(
      'inline',
      'rounded-sm smoothing-default',
      'font-normal',
      'before:content-none after:content-none',
      'px-1.5 py-0.5',
      'break-words',
      'bg-surface-active'
    ),
  },
});

const heading = Heading;
const blockquote = Blockquote;
const hardBreak = HardBreak;
const listItem = ListItem;

const bulletList = BulletList.configure({
  HTMLAttributes: {
    class: classList('list-disc list-outside'),
  },
});

const orderedList = OrderedList.configure({
  HTMLAttributes: {
    class: classList('list-decimal list-outside'),
  },
});

const dropcursor = Dropcursor.configure({
  color: 'var(--accent-active)',
  width: 2,
});

const history = History;

const tiptapLink = TiptapLink.configure({
  openOnClick: false,
  HTMLAttributes: {
    class: classList(
      'text-muted-foreground underline underline-offset-[3px] hover:text-primary transition-colors cursor-pointer'
    ),
  },
});

const taskList = TaskList.configure({
  HTMLAttributes: {
    class: classList('not-prose pl-2'),
  },
});
const taskItem = TaskItem.configure({
  HTMLAttributes: {
    class: classList('flex items-start'),
  },
  nested: true,
});

const horizontalRule = HorizontalRule.configure({
  HTMLAttributes: {
    class: classList('mt-4 mb-6 border-t border-muted-foreground'),
  },
});

const globalDragHandle = GlobalDragHandle.configure({
  dragHandleWidth: 25,
  scrollTreshold: 100,
});

const autoJoiner = AutoJoiner.configure({
  elementsToJoin: ['bulletList', 'orderedList'],
});

export const emptyLine = Paragraph.extend({
  name: 'emptyLine',

  selectable: false,

  addAttributes() {
    return {
      class: {
        default: 'trailing-empty-line',
      },
    };
  },

  addStorage() {
    return {
      markdown: {
        serialize: (state: MarkdownSerializerState, node: Node) => {
          state.renderContent(node);
          state.closeBlock(node);
        },
      },
    };
  },
});

export const trailingNode = TrailingNode.configure({
  node: 'emptyLine',
  notAfter: [],
});

// Rendering-only extensions -- sufficient for read-only display
const renderExtensions = [
  document,
  text,
  paragraph,
  bold,
  italic,
  strike,
  code,
  heading,
  blockquote,
  hardBreak,
  listItem,
  bulletList,
  orderedList,
  tiptapLink,
  taskList,
  taskItem,
  horizontalRule,
  codeblockShiki,
  HighlightExtension,
  Color,
  TextStyle,
  TiptapUnderline,
  Mathematics,
  ...tableExtensions,
  UpdatedImage,
];

// Interactive extensions -- only needed for editable editors
const interactiveExtensions = [
  dropcursor,
  history,
  emptyLine,
  trailingNode,
  globalDragHandle,
  autoJoiner,
  CustomKeymap,
  DragSelectionHide,
  defaultSlashCommand,
  AnimateInsertions,
];

const coreExtensions = [
  ...renderExtensions,
  LinkHoverExtension,
  ...interactiveExtensions,
];

export type ImageUploadBucket = ImageUploadExtensionOptions;

export interface DefaultExtensionsConfig {
  /** Custom placeholder text. Omit to use default. */
  placeholder?: {
    text?: string;
    onlyWhenEmpty?: boolean;
  };
  imageUploadBucket?: ImageUploadBucket;
  editorId?: string;
  /** Extension names to exclude from the default set */
  exclude?: string[];
  mode?: 'full' | 'readonly';
}

export function defaultExtensions(config?: DefaultExtensionsConfig) {
  if (config?.mode === 'readonly') {
    return [
      ...renderExtensions,
      LinkHoverExtension.configure({ openOnReadOnlyClick: true }),
    ];
  }

  const imageUploadExtension = ImageUploadExtension.configure({
    entity: config?.imageUploadBucket?.entity,
    entityId: config?.imageUploadBucket?.entityId,
    resolveImageFileId: config?.imageUploadBucket?.resolveImageFileId,
  });
  const cursorRestore = CursorRestore.configure({
    editorId: config?.editorId,
  });
  const excludeSet = config?.exclude?.length ? new Set(config.exclude) : null;
  const filtered = excludeSet
    ? coreExtensions.filter((ext) => !excludeSet.has(ext.name))
    : coreExtensions;
  const extras = [imageUploadExtension, cursorRestore, SearchAndReplace].filter(
    (ext) => !excludeSet?.has(ext.name)
  );
  const baseExtensions = [...filtered, ...extras];

  return [
    ...baseExtensions,
    Placeholder.configure({
      ...(config?.placeholder?.text && { text: config.placeholder.text }),
      ...(config?.placeholder?.onlyWhenEmpty && { onlyWhenEmpty: true }),
    }),
  ];
}

export const DEFAULT_EXTENSIONS = defaultExtensions();

export { MarkdownExtension };
